import { Printer } from "@/interfaces";
import { getPrinters, requestPrinterInfo } from "@/services/Printers.service";
import { getPrinterDescription, naturalCompare } from "@/utils/printer.utils";
import { QueryClient, useQueries, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, MaybeRefOrGetter, toValue, watch } from "vue";

const PRINTER_INFO_STALE_MS = 5 * 60 * 1000;

const printerInfoQueryOptions = {
  staleTime: PRINTER_INFO_STALE_MS,
  gcTime: PRINTER_INFO_STALE_MS,
  // Stop polling after a failure until the page is refreshed (or the query is reset).
  refetchInterval: (query: { state: { status: string } }) => (query.state.status === "error" ? false : PRINTER_INFO_STALE_MS),
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: false,
} as const;

export function patchPrinterInCache(queryClient: QueryClient, updatedPrinter: Printer) {
  if (!updatedPrinter?.id) return;

  queryClient.setQueryData<Printer[]>(["printers"], (old) => {
    if (!old?.length) return old;

    const index = old.findIndex((printer) => printer.id === updatedPrinter.id);
    if (index === -1) return [...old, updatedPrinter];

    const next = [...old];
    next[index] = { ...old[index], ...updatedPrinter };
    return next;
  });
}

export function usePrintersBasicInfo() {
  return useQuery({
    queryKey: ["printers"],
    queryFn: () => getPrinters("list"),
    gcTime: PRINTER_INFO_STALE_MS,
    staleTime: PRINTER_INFO_STALE_MS,
  });
}

export default function usePrinters() {
  const visiblePrinters = computed(() => {
    return printers.value?.filter((printer) => !printer.hidden).sort((a, b) => naturalCompare(getPrinterDescription(a), getPrinterDescription(b))) || [];
  });
  const {
    data: printers,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<Printer[]>({
    queryKey: ["printers"],
    queryFn: () => getPrinters("list"),
    gcTime: PRINTER_INFO_STALE_MS,
    staleTime: PRINTER_INFO_STALE_MS,
  });

  return {
    printers,
    visiblePrinters,
    isLoading,
    isError,
    error,
    refetch,
  };
}

export function usePrinterInfo(printerId: MaybeRefOrGetter<number | null | undefined>) {
  const queryClient = useQueryClient();
  const resolvedPrinterId = computed(() => toValue(printerId));

  const query = useQuery({
    queryKey: computed(() => ["printer-info", resolvedPrinterId.value]),
    queryFn: () => requestPrinterInfo(resolvedPrinterId.value!),
    enabled: computed(() => !!resolvedPrinterId.value),
    ...printerInfoQueryOptions,
  });

  // Only merge when this query's data was actually fetched/updated — not when ["printers"] changes.
  watch(
    () => query.dataUpdatedAt.value,
    (dataUpdatedAt, previousUpdatedAt) => {
      if (!dataUpdatedAt || dataUpdatedAt === previousUpdatedAt || !query.data.value) return;
      patchPrinterInCache(queryClient, query.data.value);
    },
  );

  return {
    printerInfo: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function usePrintersInfo(printers: MaybeRefOrGetter<Pick<Printer, "id" | "type">[] | undefined>) {
  const queryClient = useQueryClient();

  const queries = useQueries({
    queries: computed(() =>
      (toValue(printers) ?? [])
        .filter((printer) => printer.id && printer.type !== "destination")
        .map((printer) => ({
          queryKey: ["printer-info", printer.id] as const,
          queryFn: () => requestPrinterInfo(printer.id!),
          ...printerInfoQueryOptions,
        })),
    ),
  });

  // Patch only when a printer-info query's dataUpdatedAt changes.
  // A deep watch on `queries` also fired when ["printers"] was patched (e.g. via websocket),
  // re-applying stale printer-info and overwriting the fresh update.
  watch(
    () => queries.value.map((result) => result.dataUpdatedAt),
    (timestamps, previousTimestamps) => {
      queries.value.forEach((result, index) => {
        if (!result.data) return;
        if (previousTimestamps && timestamps[index] === previousTimestamps[index]) return;
        patchPrinterInCache(queryClient, result.data);
      });
    },
  );

  return {
    isFetching: computed(() => queries.value.some((query) => query.isFetching)),
  };
}
